import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  DialogContentText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchApi } from './fetchApi';

interface Lagerort {
  id: number;
  bezeichnung: string;
  beschreibung: string | null;
  erstellt_am: string;
}

interface FormDaten {
  bezeichnung: string;
  beschreibung: string;
}

const leerForm: FormDaten = { bezeichnung: '', beschreibung: '' };

export default function LagerorteVerwaltung() {
  const [lagerorte, setLagerorte] = useState<Lagerort[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);

  // Modal: Anlegen / Bearbeiten
  const [modalOffen, setModalOffen] = useState(false);
  const [bearbeitenId, setBearbeitenId] = useState<number | null>(null);
  const [form, setForm] = useState<FormDaten>(leerForm);
  const [modalFehler, setModalFehler] = useState<string | null>(null);

  // Bestätigungsdialog: Löschen
  const [loeschenId, setLoeschenId] = useState<number | null>(null);
  const [loeschenBezeichnung, setLoeschenBezeichnung] = useState('');

  async function ladeLagerorte() {
    try {
      const res = await fetchApi('/api/lagerorte');
      const data = await res.json();
      setLagerorte(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Fehler beim Laden der Lagerorte:', err);
    }
  }

  useEffect(() => {
    ladeLagerorte();
  }, []);

  function oeffneAnlegenModal() {
    setForm(leerForm);
    setBearbeitenId(null);
    setModalFehler(null);
    setModalOffen(true);
  }

  function oeffneBearbeitenModal(l: Lagerort) {
    setForm({ bezeichnung: l.bezeichnung, beschreibung: l.beschreibung || '' });
    setBearbeitenId(l.id);
    setModalFehler(null);
    setModalOffen(true);
  }

  function schliesseModal() {
    setModalOffen(false);
  }

  function handleFormChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSpeichern() {
    setModalFehler(null);

    if (!form.bezeichnung.trim()) {
      setModalFehler('Bezeichnung ist erforderlich.');
      return;
    }

    try {
      const res = await fetchApi(
        bearbeitenId ? `/api/lagerorte/${bearbeitenId}` : '/api/lagerorte',
        {
          method: bearbeitenId ? 'PUT' : 'POST',
          body: JSON.stringify(form),
        }
      );
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        setModalFehler(data?.error || 'Fehler beim Speichern.');
        return;
      }

      setErfolg(bearbeitenId ? 'Lagerort aktualisiert.' : 'Lagerort angelegt.');
      setFehler(null);
      setModalOffen(false);
      ladeLagerorte();
    } catch (err: any) {
      setModalFehler(
        err?.message === '401'
          ? 'Session abgelaufen. Bitte neu einloggen.'
          : (err?.message || 'Unbekannter Fehler.')
      );
    }
  }

  function oeffneLoeschenDialog(l: Lagerort) {
    setLoeschenId(l.id);
    setLoeschenBezeichnung(l.bezeichnung);
    setFehler(null);
    setErfolg(null);
  }

  function schliesseLoeschenDialog() {
    setLoeschenId(null);
    setLoeschenBezeichnung('');
  }

  async function handleLoeschen() {
    if (!loeschenId) return;

    try {
      const res = await fetchApi(`/api/lagerorte/${loeschenId}`, { method: 'DELETE' });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        setFehler(data?.error || 'Löschen nicht möglich.');
        schliesseLoeschenDialog();
        return;
      }

      setErfolg('Lagerort gelöscht.');
      schliesseLoeschenDialog();
      ladeLagerorte();
    } catch (err: any) {
      setFehler(
        err?.message === '401'
          ? 'Session abgelaufen. Bitte neu einloggen.'
          : (err?.message || 'Fehler beim Löschen.')
      );
      schliesseLoeschenDialog();
    }
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Lagerorte</Typography>
        <Button variant="contained" onClick={oeffneAnlegenModal}>
          + Lagerort anlegen
        </Button>
      </Box>

      {fehler && <Alert severity="error" sx={{ mb: 2 }}>{fehler}</Alert>}
      {erfolg && <Alert severity="success" sx={{ mb: 2 }}>{erfolg}</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Bezeichnung</TableCell>
            <TableCell>Beschreibung</TableCell>
            <TableCell>Erstellt am</TableCell>
            <TableCell align="right">Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lagerorte.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center">Keine Lagerorte vorhanden.</TableCell>
            </TableRow>
          ) : (
            lagerorte.map((l) => (
              <TableRow key={l.id} hover>
                <TableCell>{l.id}</TableCell>
                <TableCell>{l.bezeichnung}</TableCell>
                <TableCell>{l.beschreibung || '—'}</TableCell>
                <TableCell>{new Date(l.erstellt_am).toLocaleDateString('de-AT')}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => oeffneBearbeitenModal(l)} title="Bearbeiten">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => oeffneLoeschenDialog(l)} title="Löschen">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Modal: Anlegen / Bearbeiten */}
      <Dialog open={modalOffen} onClose={schliesseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{bearbeitenId ? 'Lagerort bearbeiten' : 'Lagerort anlegen'}</DialogTitle>
        <DialogContent>
          {modalFehler && <Alert severity="error" sx={{ mb: 2 }}>{modalFehler}</Alert>}
          <TextField
            label="Bezeichnung"
            name="bezeichnung"
            value={form.bezeichnung}
            onChange={handleFormChange}
            fullWidth
            required
            autoFocus
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            label="Beschreibung"
            name="beschreibung"
            value={form.beschreibung}
            onChange={(e) => setForm((prev) => ({ ...prev, beschreibung: e.target.value }))}
            fullWidth
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={schliesseModal}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSpeichern}>
            {bearbeitenId ? 'Aktualisieren' : 'Anlegen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Löschen bestätigen */}
      <Dialog open={loeschenId !== null} onClose={schliesseLoeschenDialog}>
        <DialogTitle>Lagerort löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Soll <strong>{loeschenBezeichnung}</strong> wirklich gelöscht werden?
            Dies ist nur möglich, wenn keine Lagerplätze zugeordnet sind.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={schliesseLoeschenDialog}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleLoeschen}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
